import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

// Styles
import { StyledExitButton, SVGIcon } from './styles';

// Utils
import { sendEvent } from '../../utils/analytics';
import iconSrc from '../../utils/iconSrc';
import { VisuallyHidden } from '../styled/accessibility';

const QuickExitButton = ({ showQuickExitButton = false }) => {
  const [lastEscapeTime, setLastEscapeTime] = useState(0);

  const onQuickExitButtonClick = useCallback((interactionType) => {
    const onRedirection = () => {
      window.location.replace('https://www.google.com/search?q=weather');
    };

    // Show blank page and replace the title
    document.body.innerHTML = 'Loading...';
    document.title = 'weather - Google Search';

    // clear the favicon & sets the favicon to a 1x1 transparent PNG image using a Base64-encoded data URL
    const link = document.querySelector("link[rel~='icon']");
    link.href =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAwAB/ctHhYUAAAAASUVORK5CYII=';

    // For GA4 tracking
    sendEvent({
      event: 'quickExitButtonInteraction',
      interaction_type: interactionType,

      // Redirection
      event_callback: onRedirection,
    });

    // Fallback: If GTM takes too long, redirect after 1500 ms (though 2000ms is better to ensure no events are lost, but 1500ms is more promising for UX, while 1000ms is too short and may cause some events to be lost)
    setTimeout(onRedirection, 1500);
  }, []);

  const handleDoubleEscape = useCallback(
    (event) => {
      event.preventDefault();
      onQuickExitButtonClick('keyboard_keydown');
    },
    [onQuickExitButtonClick]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (showQuickExitButton && event.key === 'Escape') {
        const currentTime = Date.now();
        const DOUBLE_PRESS_THRESHOLD = 500; // Microsoft's default double-click time is 500ms

        if (currentTime - lastEscapeTime < DOUBLE_PRESS_THRESHOLD) {
          handleDoubleEscape(event);
        }

        setLastEscapeTime(currentTime);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lastEscapeTime, handleDoubleEscape, showQuickExitButton]);

  if (!showQuickExitButton) return null;

  return (
    <StyledExitButton
      role="button"
      id="quick-exit-button-fixed"
      tabIndex={0}
      type="cta-with-javascript-action"
      aria-label="Exit site"
      aria-describedby="quick-exit-description"
      onClick={() => {
        onQuickExitButtonClick('mouse_click');
      }}
      size="xl"
    >
      {/* <SVGIcon src={iconSrc('cross')} /> */}
      Exit site
      <VisuallyHidden id="quick-exit-description">
        Leave this site quickly with this button, or tap Escape key (Esc) on
        your keyboard twice.
      </VisuallyHidden>
    </StyledExitButton>
  );
};

QuickExitButton.propTypes = {
  showQuickExitButton: PropTypes.bool,
};

export default QuickExitButton;
